(function TripSummaryFlightDetails($, Adzuki)
{
	'use strict';

	const formId = 'saveaircomponent-';

	const component = {
		emits: [
			'remove-flight',
		],
		inject: [
			'getFaIconStyle',
		],
		props: {
			airlines: {
				type   : Array,
				default: [],
			},
			componentId: {
				type   : String,
				default: 'new',
			},
			datesDisplayed: {
				type   : Boolean,
				default: true,
			},
			datesRequired: {
				type   : Boolean,
				default: true,
			},
			direction: {
				type   : String,
				default: 'departure',
			},
			flight: {
				type   : Object,
				default: null,
			},
			index: {
				type   : Number,
				default: 1,
			},
			startMode: {
				type   : String,
				default: 'lookup',
			},
		},
		template: '#ts-flight-details',
		data() {
			return {
				autoArrivalDate         : null,
				mode                    : this.startMode,
				performingLookup        : false,
				lookupPerformed         : false,
				lookupResults           : [],
				departureAirportDisabled: false,
				arrivalAirportDisabled  : false,
			};
		},
		watch: {
			// TS-323 (QA round 1). In lookup mode the lookup date is the only date on
			// screen, so the stored dates follow it -- otherwise a leg seeded with a
			// default and then re-dated by the advisor would keep the stale default,
			// and the next connecting flight (which seeds from these) would get it.
			// The arrival follows only while it still matches the departure: an
			// overnight arrival a lookup filled in is left alone.
			'flight.date_lookup'(date) {
				if (this.mode !== 'lookup' || !date) {
					return;
				}

				if (!this.flight.arrival_date || this.flight.arrival_date === this.flight.departure_date) {
					this.flight.arrival_date = date;
				}

				this.flight.departure_date = date;
			},
			// TS-326. The Google Flights search fills this flight from the PARENT, so
			// the airport inputs have to follow. They are ref-driven rather than
			// v-model'd -- the mounted hook seeds them once and nothing updates them
			// afterwards, so a fill after mount would set the ids and leave the
			// visible fields blank.
			'flight.departure_airport_id'(id) {
				if (!this.$refs.departure_airport_search) {
					return;
				}

				// TS-324: a search refill clears an airport that did not resolve. With
				// no name either, the box must clear and unlock too, or it keeps the
				// previous flight's airport on screen while posting no id.
				if (!id) {
					if (!this.flight.departure_airport) {
						this.$refs.departure_airport_search.value = '';
						this.departureAirportDisabled = false;
					}

					return;
				}

				this.$refs.departure_airport_search.value = this.flight.departure_airport || '';
				this.departureAirportDisabled             = true;
				this.mode                                 = 'manual';
			},
			'flight.arrival_airport_id'(id) {
				if (!this.$refs.arrival_airport_search) {
					return;
				}

				// TS-324: a search refill clears an airport that did not resolve. With
				// no name either, the box must clear and unlock too, or it keeps the
				// previous flight's airport on screen while posting no id.
				if (!id) {
					if (!this.flight.arrival_airport) {
						this.$refs.arrival_airport_search.value = '';
						this.arrivalAirportDisabled = false;
					}

					return;
				}

				this.$refs.arrival_airport_search.value = this.flight.arrival_airport || '';
				this.arrivalAirportDisabled             = true;
				this.mode                               = 'manual';
			},
		},
		computed: {
			// TS-391 (QA round 2). Derived, not stored: "auto" means the arrival still
			// holds exactly the date we copied in. A stored flag only cleared when the
			// arrival picker emitted `changed`, so any other way the arrival changed
			// left it stuck on auto and the next departure change overwrote the
			// advisor's date -- QA saw that on rail and transfers.
			arrivalAutoFilled() {
				return Boolean(this.flight.arrival_date) && this.flight.arrival_date === this.autoArrivalDate;
			},
			directionLabel() {
				return this.direction === 'departure' ? 'Departure' : 'Return';
			},
			directionVerb() {
				return this.direction === 'departure' ? 'Departing' : 'Arriving';
			},
			modeTitle() {
				return this.mode === 'lookup' ? 'Flight Lookup' : 'Manually Enter Flight Details';
			},
			formId() {
				return formId + this.direction + '-' + this.index;
			},
			formName() {
				return 'form_component_data[SaveAirComponent][' + this.direction + '][' + this.index + ']';
			},
		},
		methods: {
			// TS-391. Copies the departure date into an EMPTY or still-auto-filled
			// arrival on a new leg, and marks it, so the template can say so -- an
			// overnight leg used to ship with the wrong arrival date unless the advisor
			// noticed. An arrival the advisor typed is never overwritten.
			departureDateChanged(date) {
				if (this.componentId !== 'new') {
					return;
				}

				if (!date) {
					return;
				}

				if (this.flight.arrival_date && !this.arrivalAutoFilled) {
					return;
				}

				const departureDate = moment(date).format('YYYY-MM-DD');

				this.flight.arrival_date = departureDate;
				this.autoArrivalDate = departureDate;
			},
			formatDate(date) {
				return moment(date).format('MM/DD/YYYY');
			},
			formatTime(time) {
				return moment(time).format('h:mm a');
			},
			async lookupFlight() {
				const airlineId     = this.flight.airline_lookup;
				const flightNumber  = this.flight.flight_lookup;
				const departureDate = this.flight.date_lookup;

				if (!airlineId || !flightNumber || !departureDate) {
					return Adzuki.StatusMessage('error', `Please enter all flight details`);
				}

				const url = '/FlightStats/airSearch/' + airlineId + '/' + flightNumber + '/' + departureDate;

				try {
					this.performingLookup = true;
					this.lookupPerformed  = true;

					const response = await Adzuki.Ajax.get(url);

					if (response.flights) {
						this.lookupResults = response.flights;
					}
				} catch (error) {
					return Adzuki.StatusMessage('error', `Error retrieving flight details: ${error.message}`);
				} finally {
					this.performingLookup = false;
				}
			},
			removeFlight() {
				Adzuki.CustomConfirm.destructive({
					title           : 'Remove this flight?',
					body            : 'Its details will be removed from this component.',
					confirm_btn_text: 'Remove flight',
					confirm         : () => {
						this.$emit('remove-flight', this.direction, this.index);
					},
				});
			},
			resetArrivalAirport() {
				this.arrivalAirportDisabled             = false;
				this.flight.arrival_airport             = null;
				this.flight.arrival_airport_id          = null;
				this.$refs.arrival_airport_search.value = '';
			},
			resetDepartureAirport() {
				this.departureAirportDisabled             = false;
				this.flight.departure_airport             = null;
				this.flight.departure_airport_id          = null;
				this.$refs.departure_airport_search.value = '';
			},
			selectLookupFlight(result, index) {
				const flight = this.lookupResults[index];

				this.flight.airline_id    = flight.airline.id;
				this.flight.flight_number = flight.flight_number;

				if (flight.airports.departure) {
					// set departure airport
					this.flight.departure_airport             = flight.airports.departure.name;
					this.flight.departure_airport_id          = flight.airports.departure.id;
					this.$refs.departure_airport_search.value = flight.airports.departure.name;
					this.departureAirportDisabled             = true;

					// set departure date and time
					this.flight.departure_date = moment(flight.departure_time).format('YYYY-MM-DD');
					this.flight.departure_time = moment(flight.departure_time).format('HH:mm:ss');
				}

				if (flight.airports.arrival) {
					// set arrival airport
					this.flight.arrival_airport             = flight.airports.arrival.name;
					this.flight.arrival_airport_id          = flight.airports.arrival.id;
					this.$refs.arrival_airport_search.value = flight.airports.arrival.name;
					this.arrivalAirportDisabled             = true;

					// set arrival date and time
					this.flight.arrival_date = moment(flight.arrival_time).format('YYYY-MM-DD');
					this.flight.arrival_time = moment(flight.arrival_time).format('HH:mm:ss');
				}

				this.mode = 'manual';
			},
			switchMode() {
				this.mode = this.mode === 'lookup' ? 'manual' : 'lookup';
			},
		},
		mounted() {
			// departure airport typeahead
			Adzuki.Typeahead(
				this.$refs.departure_airport_search,
				'/Airports/search/',
				(item, $self, typeahead) => {
					$self.val(item.name);
					// TS-389. The name must be set BEFORE the id: the watcher above
					// re-seeds the visible field from it, so leaving it null blanks
					// the field the typeahead just filled and then locks it.
					this.flight.departure_airport    = item.name;
					this.flight.departure_airport_id = item.id;
					this.departureAirportDisabled    = true;
				}
			);

			// arrival airport typeahead
			Adzuki.Typeahead(
				this.$refs.arrival_airport_search,
				'/Airports/search/',
				(item, $self, typeahead) => {
					$self.val(item.name);
					// TS-389. See the note on the departure typeahead above.
					this.flight.arrival_airport    = item.name;
					this.flight.arrival_airport_id = item.id;
					this.arrivalAirportDisabled    = true;
				}
			);

			// if details are already set, set the mode to manual and preset the flight details
			if (this.flight.airline_id) {
				this.mode = 'manual';

				// set departure airport
				if (this.flight.departure_airport_id) {
					this.$refs.departure_airport_search.value = this.flight.departure_airport;
					this.departureAirportDisabled             = true;
				}

				// set arrival airport
				if (this.flight.arrival_airport_id) {
					this.$refs.arrival_airport_search.value = this.flight.arrival_airport;
					this.arrivalAirportDisabled             = true;
				}
			}
		},
	};

	Adzuki.VueComponents.TripSummaryFlightDetails = component;
}(jQuery, window.Adzuki || {}));
